/**
 * The runtime injected into the preview iframe.
 *
 * The frame is sandboxed WITHOUT `allow-same-origin`, deliberately: generated
 * code is untrusted, and same-origin would let it read the session token out
 * of the parent's localStorage. The cost is that the frame runs on an opaque
 * origin, where the Storage API is not merely empty but absent — touching
 * `window.localStorage` throws a SecurityError on the property getter itself.
 *
 * That last detail is what makes this necessary rather than optional:
 *
 *  - It throws on ACCESS, so the usual guards do not help. `typeof
 *    localStorage !== 'undefined'` throws. `if (window.localStorage)` throws.
 *  - It therefore throws during startup, before anything renders, and an
 *    uncaught exception there leaves an empty document with no visible error.
 *    The panel showed a blank white rectangle and no way to find out why.
 *
 * Assigning a replacement does not work either. On an opaque origin the
 * property is an accessor on the prototype whose getter throws, so
 * `window.localStorage = shim` either throws or is silently discarded; it has
 * to be redefined with `Object.defineProperty`.
 *
 * The shim is per-frame and in-memory, so preview state resets on reload.
 * That is the correct behaviour here — a preview should not accumulate
 * state — and it is the only behaviour available without same-origin.
 */

const RUNTIME = `<script>
(function () {
  function memoryStorage() {
    var data = Object.create(null);
    return {
      getItem: function (k) { k = String(k); return k in data ? data[k] : null; },
      setItem: function (k, v) { data[String(k)] = String(v); },
      removeItem: function (k) { delete data[String(k)]; },
      clear: function () { data = Object.create(null); },
      key: function (i) { var keys = Object.keys(data); return i < keys.length ? keys[i] : null; },
      get length() { return Object.keys(data).length; }
    };
  }

  ['localStorage', 'sessionStorage'].forEach(function (name) {
    try {
      // Reading the property is itself the thing that throws.
      var probe = window[name];
      probe.setItem('__catalina_probe__', '1');
      probe.removeItem('__catalina_probe__');
      return;
    } catch (err) { /* unavailable — fall through and replace it */ }

    try {
      Object.defineProperty(window, name, {
        value: memoryStorage(),
        configurable: true
      });
    } catch (err) { /* nothing further can be done for this frame */ }
  });

  // Links between the app's own pages. The frame has no URL, so a relative
  // href would resolve against the parent and navigate the preview to a 404 —
  // the same trap that broke stylesheets. The click is caught and handed up
  // instead, and the panel renders the requested page.
  document.addEventListener('click', function (event) {
    var link = event.target && event.target.closest ? event.target.closest('a') : null;
    if (!link) return;

    var href = link.getAttribute('href');
    if (!href) return;
    // Anchors, other origins, and anything that is not a page are left alone —
    // mailto: and tel: in particular have to keep working.
    if (href.charAt(0) === '#') return;
    if (/^(?:[a-z][a-z0-9+.-]*:)?\\/\\//i.test(href) || /^(mailto|tel|sms):/i.test(href)) return;
    if (!/\\.html?($|[?#])/i.test(href)) return;

    event.preventDefault();
    try {
      parent.postMessage({
        source: 'catalina-preview',
        path: href.split(/[?#]/)[0].replace(/^\\.\\//, '')
      }, '*');
    } catch (err) { /* no parent to tell */ }
  });

  /**
   * Tells the panel what went wrong, as well as showing it here.
   *
   * The box below is for whoever is looking at the frame. This is for the
   * Builder, which can act on it: the app has just been RUN, and what a run
   * produces is the only evidence that exists about whether the code works.
   * Without this the observation died inside the iframe and the product had no
   * way to know its own output was broken.
   *
   * Every failure is sent, not only the first — the box shows one because a
   * stack of red panels helps nobody, but a fix wants the whole list.
   */
  function tell(kind, detail, resource) {
    try {
      parent.postMessage({
        source: 'catalina-preview',
        error: { kind: kind, detail: String(detail).slice(0, 4000), resource: resource || null }
      }, '*');
    } catch (err) { /* no parent to tell */ }
  }

  var reported = false;
  function report(message) {
    if (reported) return;
    reported = true;
    var box = document.createElement('div');
    box.setAttribute('role', 'alert');
    box.style.cssText = 'position:fixed;left:0;right:0;bottom:0;z-index:2147483647;' +
      'background:#1c1917;color:#fca5a5;border-top:2px solid #FF3B30;' +
      'font:12px/1.5 ui-monospace,SFMono-Regular,Menlo,monospace;' +
      'padding:10px 14px;max-height:45%;overflow:auto;white-space:pre-wrap';
    box.textContent = message;
    (document.body || document.documentElement).appendChild(box);
  }

  // Capture phase, because a <script> or <link> that fails to LOAD fires its
  // error on the element and never reaches window in the bubble phase. Those
  // were previously invisible: a CDN tag that 404s produced a blank preview
  // and no message at all.
  window.addEventListener('error', function (event) {
    var el = event.target;
    if (el && el !== window && el.tagName) {
      var url = el.src || el.href;
      if (url) {
        // A path that is not on a CDN is one of the app's own files, and
        // telling the user to stop using a CDN would send them after the wrong
        // problem entirely.
        var external = /^(?:[a-z]+:)?\\/\\//i.test(el.getAttribute('src') || el.getAttribute('href') || '');
        tell(external ? 'resource-external' : 'resource-missing',
          (el.tagName || '').toLowerCase() + ' failed to load', url);
        report('A resource failed to load:\\n\\n' + url + '\\n\\n' + (external
          ? 'The app cannot run without it. Check the URL, or inline the code instead of loading it from a CDN.'
          : 'This is one of the app\\'s own files, and it is missing from the project. Ask for it to be created, or for its contents to be moved into index.html.'));
        return;
      }
    }

    var detail = (event.error && event.error.stack) || event.message || 'Unknown error';

    // A cross-origin script that throws is collapsed by the browser to exactly
    // "Script error." with no file, line or column — a deliberate leak guard,
    // and useless to the person reading it. Since the generated apps are told
    // to use CDN tags, this is the common case rather than the rare one, so it
    // gets an explanation instead of the bare string.
    if (!event.error && !event.filename && /^Script error/.test(event.message || '')) {
      detail = 'A script loaded from another domain threw an error.\\n\\n' +
        'The browser hides the details of cross-origin failures, so there is no ' +
        'line number to show. This is usually JSX or Babel that failed to compile, ' +
        'or a CDN library used before it finished loading.';
    }

    var where = event.filename
      ? '\\n\\nat ' + event.filename + ' line ' + event.lineno + ', column ' + event.colno
      : '';
    tell('exception', detail + where);
    report('This app failed to run:\\n\\n' + detail + where);
  }, true);

  window.addEventListener('unhandledrejection', function (event) {
    var reason = event.reason;
    var detail = (reason && (reason.stack || reason.message)) || String(reason);
    tell('rejection', detail);
    report('An async operation failed:\\n\\n' + detail);
  });
})();
<\/script>`;

/**
 * Resolves a project's own files into the document.
 *
 * The frame renders through `srcDoc`, which has no URL of its own, so a
 * relative `href` resolves against the PARENT page — `styles.css` becomes
 * `catalina-ai.pages.dev/builder/styles.css` and 404s. It never came up while
 * every app was one self-contained file; multi-file builds broke the preview
 * for every app that had a stylesheet.
 *
 * The generated code is not at fault and is not corrected: deployed to Pages
 * those files really are siblings and the relative paths are right. This is the
 * preview harness catching up with what the builds now look like.
 */
function resolveSiblings(html, files) {
  if (!Array.isArray(files) || files.length === 0) return html;

  const byPath = new Map(files.map((file) => [file.path.replace(/^\.?\//, ''), file.content]));

  /** Same-origin siblings only — a CDN or an absolute path is left alone. */
  const lookup = (raw) => {
    if (!raw || /^(?:[a-z]+:)?\/\//i.test(raw) || raw.startsWith('/') || raw.startsWith('data:')) {
      return null;
    }
    const path = raw.split(/[?#]/)[0].replace(/^\.\//, '');
    return byPath.has(path) ? byPath.get(path) : null;
  };

  const withCss = html.replace(/<link\b([^>]*)>/gi, (tag, attrs) => {
    if (!/\brel\s*=\s*["']?stylesheet/i.test(attrs)) return tag;
    const href = attrs.match(/\bhref\s*=\s*["']([^"']+)["']/i);
    const css = href && lookup(href[1]);
    if (css == null) return tag;
    // A stylesheet cannot contain `</style` legitimately, but escaping it costs
    // nothing and stops a stray one closing the block early.
    return `<style>\n${css.replace(/<\/style/gi, '<\\/style')}\n</style>`;
  });

  return withCss.replace(/<script\b([^>]*)>\s*<\/script>/gi, (tag, attrs) => {
    const src = attrs.match(/\bsrc\s*=\s*["']([^"']+)["']/i);
    const js = src && lookup(src[1]);
    if (js == null) return tag;

    const rest = attrs.replace(/\bsrc\s*=\s*["'][^"']*["']/i, '').trim();

    // `defer` and `async` are properties of a script WITH a src, and inlining
    // silently drops them — a deferred script in <head> would then run before
    // the DOM it queries exists, which looks exactly like a broken app. Keeping
    // those as a data: URL preserves the timing the author asked for.
    if (/\b(defer|async)\b/i.test(rest)) {
      const encoded = encodeURIComponent(js);
      return `<script ${rest} src="data:text/javascript;charset=utf-8,${encoded}"></script>`;
    }

    return `<script${rest ? ` ${rest}` : ''}>\n${js.replace(/<\/script/gi, '<\\/script')}\n</script>`;
  });
}

/**
 * Hosts verified to answer with `Access-Control-Allow-Origin: *`.
 *
 * This list is an allowlist rather than a rule, and the distinction is the
 * whole point. `crossorigin="anonymous"` turns the request into a CORS request:
 * if the response carries no `Access-Control-Allow-Origin`, the browser does
 * not fall back to loading it normally — it BLOCKS the script outright.
 *
 * `cdn.tailwindcss.com` is the case that proves it. It answers 200 with no CORS
 * header at all, so marking it crossorigin stops Tailwind loading and takes the
 * whole app down. It is deliberately absent below: a masked error on a working
 * app beats a precise error on a broken one.
 *
 * Everything here is a general-purpose package CDN, for which serving
 * cross-origin is the product rather than an incidental. Verified 2026-08-12;
 * a host must be re-checked before being added.
 */
const CORS_SAFE_HOSTS = [
  'unpkg.com',
  'cdn.jsdelivr.net',
  'cdnjs.cloudflare.com',
  'esm.sh',
];

/**
 * Marks known-safe external scripts CORS-eligible so their errors survive.
 *
 * `window.onerror` reports a cross-origin script's failure as the bare string
 * "Script error." — no message, no file, no line. React UMD and Babel come from
 * these hosts, and a JSX compile failure is exactly the error worth reading, so
 * opting them in buys a real stack trace.
 *
 * Tags carrying their own `crossorigin` are left alone, and relative or inline
 * scripts are untouched: they are same-origin to the frame and were never
 * masked.
 */
function withCorsScripts(html) {
  return html.replace(/<script\b([^>]*)>/gi, (tag, attrs) => {
    const src = attrs.match(/\bsrc\s*=\s*["']?((?:https?:)?\/\/[^"'\s>]+)/i);
    if (!src) return tag;
    if (/\bcrossorigin\b/i.test(attrs)) return tag;

    const host = src[1].replace(/^(?:https?:)?\/\//i, '').split(/[/?#]/)[0].toLowerCase();
    if (!CORS_SAFE_HOSTS.includes(host)) return tag;

    return `<script ${attrs.trim()} crossorigin="anonymous">`;
  });
}

/**
 * Puts the runtime in front of the generated document.
 *
 * It has to run before any other script, including the CDN tags in `<head>`,
 * because those can touch storage on load. Injecting after `<head>` is the
 * earliest safe point; a document with no `<head>` gets it prepended, which
 * the parser then hoists into the head it synthesises.
 */
export function withPreviewRuntime(rawHtml, files) {
  if (typeof rawHtml !== 'string' || rawHtml.trim() === '') return rawHtml;

  const html = withCorsScripts(resolveSiblings(rawHtml, files));
  const head = html.match(/<head[^>]*>/i);
  if (head) {
    const at = head.index + head[0].length;
    return html.slice(0, at) + RUNTIME + html.slice(at);
  }

  const htmlTag = html.match(/<html[^>]*>/i);
  if (htmlTag) {
    const at = htmlTag.index + htmlTag[0].length;
    return html.slice(0, at) + RUNTIME + html.slice(at);
  }

  return RUNTIME + html;
}
